import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReportStore } from '../../stores/useReportStore';
import { useAuditStore } from '../../stores/useAuditStore';
import { useProcessingStore } from '../../stores/useProcessingStore';
import { useQuestionnaireStore } from '../../stores/useQuestionnaireStore';
import { apiFetch, fetchAuditReport, fetchAuditStatus } from '../../api/client';
import { supabase } from '../../lib/supabase';
import { normalizeReportData } from '../../utils/normalizeReport';
import ReportSkeleton from './ReportSkeleton';
import ExecutiveSummary from './tabs/ExecutiveSummary';
import Performance from './tabs/Performance';
import Analysis from './tabs/Analysis';
import SeoAeo from './tabs/SeoAeo';
import MarketSizing from './tabs/MarketSizing';
import StrategyRoadmap from './tabs/StrategyRoadmap';
import ReportHeader from './widgets/ReportHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { FileDown, Clock } from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';

function DownloadButton({ label, report }: { label: string; report?: { id: string; filename: string } }) {
  const download = async () => {
    if (!report) return;
    // authedFetch renews the token and retries once before treating a 401 as a
    // dead session — a download must not end the user's session on its own.
    const { authedFetch } = await import('../../api/client');
    const res = await authedFetch(`/api/reports/${report.id}/download`, {
      redirect: 'follow',
    });
    if (res.status === 401) return;
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = report.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tooltipText = report ? `Download ${label}` : `${label} not available`;

  return (
    <div className="relative group">
      <Button
        variant="outline"
        size="sm"
        className="h-9 gap-1.5 rounded-[10px] px-3.5 text-[13px] font-medium cursor-pointer"
        disabled={!report}
        onClick={download}
      >
        <FileDown className="size-3.5" />
        {label}
      </Button>
      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 rounded bg-foreground text-background text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        {tooltipText}
      </div>
    </div>
  );
}

// Define which data keys each tab needs to have content
const TAB_DATA_REQUIREMENTS: Record<string, string[]> = {
  'r-executive': ['healthScore', 'healthscore'],
  'r-performance': ['channelBenchmarks', 'funnel', 'scorecard'],
  'r-analysis': ['swot', 'businessAreas', 'peers', 'macroRisks'],
  'r-seo': ['seo', 'aeo'],
  'r-market': ['marketSizing', 'scenarios'],
  'r-strategy': ['pillars', 'gaps'],
};

function hasData(data: Record<string, unknown> | null, keys: string[]): boolean {
  if (!data) return false;
  if (keys.length === 0) return true; // Always show if no requirements
  return keys.some((key) => {
    const val = data[key];
    if (val === null || val === undefined) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return true;
  });
}

const ALL_TABS = [
  { id: 'r-executive' as const, label: 'Executive Summary' },
  { id: 'r-performance' as const, label: 'Performance' },
  { id: 'r-analysis' as const, label: 'Analysis' },
  { id: 'r-seo' as const, label: 'SEO & AEO' },
  { id: 'r-market' as const, label: 'Market Sizing' },
  { id: 'r-strategy' as const, label: 'Strategy & Roadmap' },
];

type ReviewGate = 'loading' | 'pending' | 'approved' | 'no_review' | 'no_output';

interface PendingReviewInfo {
  status: 'pending' | 'in_review' | 'changes_requested';
  approvedAt: null;
}

export default function ReportView() {
  const { activeSubTab, setSubTab, setReportData, setReportFiles } = useReportStore();
  const reportData = useReportStore((s) => s.reportData);
  const reportFiles = useReportStore((s) => s.reportFiles);
  const userRole = useAuthStore((s) => s.userRole);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const navigate = useNavigate();
  const { chatId: urlChatId } = useParams<{ chatId: string }>();

  const [reviewGate, setReviewGate] = useState<ReviewGate>('loading');
  const [pendingInfo, setPendingInfo] = useState<PendingReviewInfo | null>(null);
  // True while the load effect is in flight. The gate opens (step 2) before
  // the report data arrives (step 3), so without this flag the page flashed
  // "No Report Data" between the two.
  const [dataLoading, setDataLoading] = useState(true);

  const handleStartNew = () => {
    useAuditStore.getState().reset();
    useProcessingStore.getState().reset();
    useQuestionnaireStore.getState().reset();
    setReportData(null as any);
    navigate('/connect');
  };

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Re-arm the skeleton on every (re)load — deps changing (auth arriving,
      // navigation to another chat's report) restarts the fetch sequence.
      setDataLoading(true);

      // Step 1 — fetch the workflow snapshot to learn the latest review status.
      // Employees bypass the gate (they need to see content to review it).
      // Leads only see content once their review is approved.
      let isApproved = false;
      let info: PendingReviewInfo | null = null;
      let hasReview = false;
      let workflowStage: string | null = null;

      if (userId) {
        const { data: snap } = await supabase.rpc('get_lead_workflow', { p_user_id: userId });
        if (snap) {
          workflowStage = (snap as any).current_stage ?? null;
          const review = (snap as any).latest_review;
          if (review) {
            hasReview = true;
            isApproved = review.status === 'approved';
            if (!isApproved) {
              info = { status: review.status, approvedAt: null };
            }
          }
        }
      }

      if (cancelled) return;

      const isEmployee = userRole === 'employee';

      // A run that is still going belongs on the progress screen, not here.
      // The `stageHasReport` fallback below keys on workflow stage 'ai', which
      // covers "analysis RUNNING" as well as "analysis produced a report" — so
      // without this check a lead who opens /report mid-generation is told
      // "your report has been generated and is queued for review" while it is
      // still being written. Ask the server what is actually happening.
      const chatForStatus = urlChatId
        || useAuditStore.getState().chatId
        || sessionStorage.getItem('analysis_chat_id');
      let runInFlight = false;
      let producedNothing = false;
      if (!isEmployee && chatForStatus && workflowStage === 'ai' && !hasReview) {
        try {
          const status = await fetchAuditStatus(chatForStatus);
          if (cancelled) return;
          // Deliverables win: a finished run with a renderable report is a
          // report, whatever the stage says.
          const empty = !status.has_deliverables && !status.has_renderable_report;
          runInFlight = empty && (status.is_running || !status.is_complete);
          // Finished, but nothing came out of it. The run can end with `done`
          // and no `error` while saving zero blocks, and the review row is only
          // created when a compiled_report is written — so this state reaches
          // the lead as "your report has been generated", which is false.
          producedNothing = empty && !runInFlight;
        } catch {
          /* status unavailable — fall through to the existing behaviour */
        }
      }
      if (runInFlight && chatForStatus) {
        navigate(`/processing/${chatForStatus}`, { replace: true });
        return;
      }
      if (producedNothing) {
        // Deliberately NOT a redirect to /processing: that screen would see a
        // complete run and bounce straight back here, ping-ponging the user.
        setReviewGate('no_output');
        setPendingInfo(null);
        setReportData(null as any);
        setReportFiles([]);
        return;
      }
      // Block leads in two cases:
      //  (a) a non-approved review row exists (the canonical post-migration
      //      flow — `audit_results_create_review` trigger creates a pending
      //      review the moment `compiled_report` is saved).
      //  (b) **defensive fallback** — analysis already produced a renderable
      //      report (workflow_stage != 'signed' / 'connected') but no review
      //      row exists yet. Without this branch a lead would briefly see
      //      the unreviewed dashboard if the trigger hasn't been deployed
      //      or the row didn't get created for any reason.
      const stageHasReport =
        workflowStage === 'ai' ||
        workflowStage === 'review' ||
        workflowStage === 'approved' ||
        workflowStage === 'live';
      const blocked = !isEmployee && (
        (hasReview && !isApproved) ||
        (!hasReview && stageHasReport)
      );

      if (blocked) {
        setReviewGate('pending');
        // When we don't have a real review row yet, show a generic "awaiting
        // reviewer" status so the placeholder copy still makes sense.
        setPendingInfo(info ?? { status: 'pending', approvedAt: null });
        // Do NOT load report data, do NOT fire view tracking — content is hidden.
        setReportData(null as any);
        setReportFiles([]);
        return;
      }

      setReviewGate(hasReview || isEmployee ? 'approved' : 'no_review');
      setPendingInfo(null);

      // Step 2 — load report files (PDF/PPTX) and fire view tracking.
      const cid = urlChatId
        || useAuditStore.getState().chatId
        || sessionStorage.getItem('analysis_chat_id');
      const reportsUrl = cid ? `/api/reports?chat_id=${cid}` : '/api/reports';
      try {
        const r = await apiFetch<{ reports: Array<{ id: string; filename: string; file_size?: number }> }>(reportsUrl);
        if (cancelled) return;
        const reports = r.reports || [];
        setReportFiles(reports);
        // Mark as viewed only when the lead actually sees the approved report.
        // Employees don't move the workflow into 'live'.
        if (!isEmployee && isApproved) {
          const latest = reports[0];
          if (latest?.id) {
            supabase.rpc('mark_report_viewed', { p_report_id: latest.id }).then(() => {}, () => {});
          }
        }
      } catch { /* tolerate empty list */ }

      // Step 3 — load report data.
      // If the URL pins a specific chat_id, ONLY load that chat — never fall
      // back to /audit/latest/report (would show another chat's data and used
      // to leak across users). Latest-fallback is allowed only when the
      // viewer landed on /report without an explicit chat_id.
      const explicitChatId = urlChatId
        || useAuditStore.getState().chatId
        || sessionStorage.getItem('analysis_chat_id');
      if (explicitChatId) {
        try {
          const raw = await fetchAuditReport(explicitChatId);
          if (!cancelled && raw && Object.keys(raw).length > 0 && !('error' in raw)) {
            setReportData(normalizeReportData(raw as Record<string, unknown>) as any);
            return;
          }
        } catch { /* empty / unauthorized → render empty state */ }
        if (!cancelled) setReportData(null as any);
        return;
      }

      try {
        const raw = await apiFetch<Record<string, unknown>>('/api/audit/latest/report');
        if (!cancelled && raw && Object.keys(raw).length > 3 && !('error' in raw)) {
          setReportData(normalizeReportData(raw) as any);
          return;
        }
      } catch { /* fall through */ }

      if (!cancelled) setReportData(null as any);
    }

    run().finally(() => { if (!cancelled) setDataLoading(false); });
    return () => { cancelled = true; };
  }, [setReportData, setReportFiles, urlChatId, userId, userRole]);

  // Filter tabs to only show those with real data
  const visibleTabs = useMemo(() => {
    if (!reportData) return [];
    const data = reportData as unknown as Record<string, unknown>;
    return ALL_TABS.filter((tab) => hasData(data, TAB_DATA_REQUIREMENTS[tab.id]));
  }, [reportData]);

  // Auto-select first visible tab if current selection is hidden
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find((t) => t.id === activeSubTab)) {
      setSubTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeSubTab, setSubTab]);

  const renderSubTab = () => {
    switch (activeSubTab) {
      case 'r-executive': return <ExecutiveSummary />;
      case 'r-performance': return <Performance />;
      case 'r-analysis': return <Analysis />;
      case 'r-seo': return <SeoAeo />;
      case 'r-market': return <MarketSizing />;
      case 'r-strategy': return <StrategyRoadmap />;
      default: return visibleTabs.length > 0 ? <ExecutiveSummary /> : null;
    }
  };

  if (reviewGate === 'no_output') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 py-16">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Clock className="size-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            No report was produced
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            The analysis finished without generating any results — usually because
            the connected sources returned too little data to work with. Nothing
            has been queued for review.
          </p>
          <button
            type="button"
            onClick={handleStartNew}
            className="mt-5 cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Check your connections
          </button>
        </div>
      </div>
    );
  }

  if (reviewGate === 'pending') {
    const headline = pendingInfo?.status === 'changes_requested'
      ? 'Report is being revised'
      : 'Report is under review';
    const subtext = pendingInfo?.status === 'changes_requested'
      ? 'Our team has requested changes and is updating your report. You will receive an email as soon as the revised version is approved.'
      : pendingInfo?.status === 'in_review'
      ? 'Our team is reviewing your report right now. You will receive an email the moment it is approved.'
      : 'Your report has been generated and is queued for our team to review. You will receive an email the moment it is approved.';

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 py-16">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Clock className="size-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">{headline}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{subtext}</p>
        </div>
      </div>
    );
  }

  if (reviewGate === 'loading') {
    return <ReportSkeleton />;
  }

  if (!reportData) {
    if (dataLoading) {
      return <ReportSkeleton />;
    }
    return (
      <div className="px-8 py-16 text-center text-muted-foreground">
        <h3 className="text-lg font-semibold text-foreground">No Report Data</h3>
        <p className="mt-2">Run an analysis first to generate your business audit report.</p>
      </div>
    );
  }

  const rawReport = reportData as unknown as Record<string, unknown>;
  const meta = (rawReport._meta ?? {}) as { generated_at?: string; version?: number };
  const healthScoreValue = (() => {
    const hs = rawReport.healthScore as { score?: unknown } | undefined;
    return typeof hs?.score === 'number' ? hs.score : null;
  })();
  const sourceNames = (() => {
    const dq = rawReport.dataQuality;
    if (!Array.isArray(dq)) return [];
    return dq
      .map((d) => (d && typeof d === 'object' ? String((d as { platform?: unknown; name?: unknown }).platform ?? (d as { name?: unknown }).name ?? '') : ''))
      .filter(Boolean)
      .slice(0, 8);
  })();

  return (
    <div>
      {visibleTabs.length > 0 ? (
        <>
          <ReportHeader
            healthScore={healthScoreValue}
            sources={sourceNames}
            generatedAt={meta.generated_at}
            version={meta.version}
            actions={
              <DownloadButton
                label="PDF"
                report={reportFiles.find((r: { filename: string }) => r.filename?.endsWith('.pdf'))}
              />
            }
          />

          {/* Sub-tabs — sticky beneath header, compact */}
          <div className="sticky z-10 bg-background border-b border-border transition-[top] duration-300" style={{ top: 'var(--header-h, 0px)' }}>
            <div className="flex items-center gap-4 px-8 overflow-hidden">
              <Tabs
                value={activeSubTab}
                onValueChange={(value: string) => setSubTab(value as typeof activeSubTab)}
                className="min-w-0 flex-1 overflow-x-auto scrollbar-hide"
              >
                <TabsList variant="line" className="h-9 [&>*]:text-xs [&>*]:py-1.5 [&>*]:whitespace-nowrap">
                  {visibleTabs.map((tab) => (
                    <TabsTrigger key={tab.id} value={tab.id}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div
            className="animate-in fade-in duration-300 px-8 pt-6"
            key={activeSubTab}
          >
            {renderSubTab()}
          </div>
        </>
      ) : (
        <div className="px-8 py-16 text-center text-muted-foreground">
          <p>No report sections available yet. Run an analysis to populate the report.</p>
        </div>
      )}
    </div>
  );
}
